import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { disconnectGoogleConnection, listGoogleConnections, type GoogleConnectionDto } from '../lib/api';
import { useAuth } from '../lib/auth';
import { GBIBadge, GBIBtn, GBICard, GBILabel, T, sharedStyles } from '../components/Shared';

const ERROR_LABELS: Record<string, string> = {
  oauth_denied:            'You cancelled the Google sign-in.',
  oauth_invalid:           'OAuth state was invalid. Please try again.',
  oauth_unconfigured:      'Google OAuth client is not configured on the server.',
  oauth_token_failed:      'Google rejected the authorization code.',
  oauth_userinfo_failed:   'Could not fetch your Google profile.',
  oauth_no_verified_email: 'Your Google email is not verified.',
  no_refresh_token:        'Google did not return a refresh token. Disconnect this app from your Google account, then try again: https://myaccount.google.com/permissions',
};

export function AdminIntegrations({ embedded = false }: { embedded?: boolean }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [connections, setConnections] = useState<GoogleConnectionDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const errorCode = params.get('error');
  const connectedEmail = params.get('connected');

  const refresh = async () => {
    try {
      setLoadError(null);
      const c = await listGoogleConnections();
      setConnections(c);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => { void refresh(); }, []);

  // Clear ?error / ?connected from URL after a few seconds.
  useEffect(() => {
    if (errorCode || connectedEmail) {
      const t = setTimeout(() => setParams({}, { replace: true }), 8000);
      return () => clearTimeout(t);
    }
  }, [errorCode, connectedEmail, setParams]);

  const handleDisconnect = async (c: GoogleConnectionDto) => {
    if (!confirm(`Disconnect ${c.googleEmail}? Any client GBP data we pull through this connection will stop until reconnected.`)) return;
    setActionError(null);
    try {
      await disconnectGoogleConnection(c.id);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    // Own scroll container: global CSS locks body{overflow:hidden} on desktop,
    // so the page must scroll itself or content gets clipped.
    <div style={{ height: embedded ? 'auto' : '100vh', overflowY: embedded ? 'visible' : 'auto', background: '#f8f9fa', padding: embedded ? 0 : '40px 32px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {!embedded && <>
          <Link to="/admin" style={{ fontSize: 12, color: T.fgSecondary, textDecoration: 'none' }}>← Back to clients</Link>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 28, fontWeight: 700, color: T.fgPrimary, marginTop: 8, marginBottom: 4 }}>
            Google integrations
          </div>
          <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 16 }}>
            Connect each Google account that manages client GBPs. The system auto-discovers which clients each account can manage and routes API calls accordingly.
            {isAdmin ? '' : ' You can only see your own connections.'}
          </div>
        </>}

        {/* Status banners */}
        {connectedEmail && (
          <Banner color={T.green}>
            ✅ Successfully connected <strong>{connectedEmail}</strong>. Locations will appear after we sync your GBP accounts.
          </Banner>
        )}
        {errorCode && (
          <Banner color={T.magenta}>
            <strong>Connection failed:</strong> {ERROR_LABELS[errorCode] ?? errorCode}
          </Banner>
        )}
        {actionError && (
          <Banner color={T.magenta}>{actionError}</Banner>
        )}
        {loadError && (
          <Banner color={T.magenta}>Failed to load: {loadError}</Banner>
        )}

        {/* Connect button */}
        <a href="/api/integrations/google/start" style={{ textDecoration: 'none' }}>
          <GBIBtn variant="primary" style={{ marginBottom: 24 }}>
            🔵 Connect a Google account
          </GBIBtn>
        </a>

        <GBILabel>Connected accounts</GBILabel>

        {connections === null && !loadError && (
          <div style={{ color: T.fgMuted, fontSize: 13, padding: 16 }}>Loading…</div>
        )}

        {connections?.length === 0 && (
          <GBICard>
            <div style={{ color: T.fgMuted, fontSize: 13 }}>
              No Google accounts connected yet. Click "Connect a Google account" above to grant access to your GBP data.
            </div>
            <div style={{ marginTop: 14, padding: 12, background: 'rgba(245,160,32,0.06)', border: `1px solid ${T.amber}`, borderRadius: 8, fontSize: 12, color: T.amber }}>
              <strong>Note:</strong> Google needs to approve our app's GBP API access before location data appears (1-2 weeks). The Connect flow itself works now and saves your tokens — discovery + data fetch will start automatically once approval is granted.
            </div>
          </GBICard>
        )}

        {connections && connections.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {connections.map(c => (
              <ConnectionCard key={c.id} connection={c} canDelete={isAdmin || c.userId === user?.id} onDisconnect={() => handleDisconnect(c)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectionCard({ connection: c, canDelete, onDisconnect }: { connection: GoogleConnectionDto; canDelete: boolean; onDisconnect: () => void }) {
  const statusColor = c.status === 'active' ? T.green : c.status === 'error' ? T.magenta : T.fgMuted;
  const ageMs = Date.now() - c.connectedAt;
  const ageLabel = ageMs < 60_000 ? 'just now' : ageMs < 3_600_000 ? `${Math.floor(ageMs / 60_000)}m ago` : ageMs < 86_400_000 ? `${Math.floor(ageMs / 3_600_000)}h ago` : `${Math.floor(ageMs / 86_400_000)}d ago`;

  return (
    <GBICard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 15, fontWeight: 700, color: T.fgPrimary }}>{c.googleEmail}</span>
            <GBIBadge color={statusColor}>{c.status}</GBIBadge>
            {c.scopes.includes('https://www.googleapis.com/auth/business.manage') && (
              <GBIBadge color={T.cyan}>GBP scope</GBIBadge>
            )}
          </div>
          <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 4 }}>
            Connected by <strong style={{ color: T.fgSecondary }}>{c.userEmail}</strong> · {ageLabel} · {c.locationCount} location{c.locationCount === 1 ? '' : 's'} discovered
          </div>
          {c.lastError && (
            <div style={{
              marginTop: 8, padding: '6px 10px', fontSize: 11,
              background: 'rgba(232,80,26,0.06)', border: `1px solid ${T.magenta}`, borderRadius: 6,
              color: T.magenta, fontFamily: sharedStyles.fontMono,
            }}>
              {c.lastError}
            </div>
          )}
        </div>
        {canDelete && (
          <button onClick={onDisconnect} style={{
            background: 'transparent', border: `1px solid ${T.magenta}`, color: T.magenta,
            borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
          }}>Disconnect</button>
        )}
      </div>
    </GBICard>
  );
}

function Banner({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <div style={{
      marginBottom: 16, padding: '10px 14px',
      background: `${color}14`, border: `1px solid ${color}`, borderRadius: 8,
      fontSize: 13, color,
    }}>{children}</div>
  );
}
